/**
 * DXF floor-plan import utility.
 *
 * Uses the `dxf` package to tessellate every entity (lines, polylines, arcs,
 * circles, ellipses, splines, expanded block INSERTs) into flat polylines.
 *
 * COORDINATE NORMALISATION
 * ─────────────────────────
 * Real-world DXF files use document units (usually mm). A typical office
 * building is 40 000 – 100 000 mm wide, which forces a 1 % zoom. To fix this,
 * all coordinates are mapped into a fixed target canvas space whose longest
 * dimension is TARGET_CANVAS_UNITS. This makes the floor plan visible at ~80 %
 * zoom, and component default dimensions (50–160 units) are proportional to
 * real AMHS hardware in that same normalised space.
 *
 * The scale factor (raw DXF units → canvas units) is returned so the Toolbar
 * can display the real-world scale and so newly dropped components can be
 * sized correctly.
 *
 * Y-AXIS
 * ──────
 * DXF uses Y-up; Konva/canvas uses Y-down. Y is negated before normalisation.
 */
import { parseString, toPolylines, denormalise } from 'dxf';
import {
  calibrateDrawing,
  deriveCanvasCalibration,
  type DimensionSample,
  type DrawingCalibration,
} from './dxfCalibration';

export type { DrawingCalibration, CalibrationSource } from './dxfCalibration';

/** Canvas units the longest floor-plan dimension is mapped to. */
const TARGET_CANVAS_UNITS = 4000;

export interface FloorPlanBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * One drawable DXF layer, surfaced so the UI can show/hide it. The geometry of
 * a layer is referenced indirectly through `ParsedFloorPlan.polylineLayers`
 * (each polyline stores the index of the layer it belongs to).
 */
export interface DxfLayerInfo {
  /** Backend layer UUID when the parser/API provides one. */
  id?: string;
  /** Layer name as it appears in the DXF (e.g. "xtl3300$0$I-EQUIP-TOOL"). */
  name: string;
  /** AutoCAD Color Index (ACI). Negative in source means "layer off". */
  color: number;
  /** Optional semantic category emitted by the external parser. */
  category?: string;
  /** Optional semantic sub-category emitted by the external parser. */
  subcategory?: string;
  /** Whether the layer is currently rendered. */
  visible: boolean;
  /** Whether the layer was frozen in the source DXF. */
  frozen?: boolean;
  /** Whether the layer was locked in the source DXF. */
  locked?: boolean;
  /** Number of drawable polylines on this layer. */
  count: number;
}

/**
 * Lightweight per-polyline provenance, aligned 1:1 with `ParsedFloorPlan.polylines`.
 * Lets the UI answer "what is this line?" on hover without re-parsing the source.
 */
export interface DxfPolylineMeta {
  /** Source entity type (LINE, LWPOLYLINE, POLYLINE, ARC, CIRCLE). */
  type: string;
  /** Entity ACI color (256 = ByLayer, 0 = ByBlock). */
  color: number;
  /** Semantic category attached directly to the source entity. */
  category?: string;
  /** Semantic sub-category attached directly to the source entity. */
  subcategory?: string;
}

/**
 * A drawable text label (DXF TEXT entity) positioned in normalised canvas space.
 * MTEXT is not included because the external parser emits empty coordinates for it.
 */
export interface DxfTextLabel {
  /** Insertion point X in normalised canvas space. */
  x: number;
  /** Insertion point Y in normalised canvas space. */
  y: number;
  /** The text string to render. */
  text: string;
  /** Cap height in normalised canvas units (font size). */
  height: number;
  /** Rotation in degrees, already adjusted for the canvas Y-flip. */
  rotation: number;
  /** Index of the owning layer in `ParsedFloorPlan.layers`. */
  layerIndex: number;
  /** Entity ACI color (256 = ByLayer). */
  color: number;
}

export interface ParsedFloorPlan {
  /** Each entry is a flat point list [x0, y0, x1, y1, ...] in normalised canvas space. */
  polylines: number[][];
  bounds: FloorPlanBounds;
  /**
   * How many raw DXF units equal one canvas unit.
   * e.g. if scaleFactor = 20 and the DXF is in mm, 1 canvas unit = 20 mm.
   * Use this to size components: a stocker that is 2 000 mm wide should be
   * placed at width = 2000 / scaleFactor = 100 canvas units.
   */
  scaleFactor: number;
  /** Layer metadata, one entry per distinct drawable layer. */
  layers: DxfLayerInfo[];
  /**
   * Raw DXF unit used by the source document. Kept for backwards compatibility;
   * when the units are unknown this is a placeholder ('mm') and
   * `dxfUnitsPerMeter` is 0 — consult `calibration` for the authoritative,
   * possibly-unknown result.
   */
  dxfUnit: 'mm' | 'cm' | 'm' | 'in' | 'ft';
  /**
   * Number of source DXF units per meter, or 0 when the units could not be
   * determined (callers must then fall back rather than assuming millimetres).
   */
  dxfUnitsPerMeter: number;
  /**
   * Authoritative physical calibration of the drawing (drawing units ↔ real
   * world), determined from the header, dimensions, scale bars or annotations.
   * `resolved` is false when the units are genuinely unknown.
   */
  calibration: DrawingCalibration;
  /** Millimetres represented by one normalised canvas unit (null if unknown). */
  millimetersPerCanvasUnit: number | null;
  /** Canvas units per meter (null if unknown). */
  canvasUnitsPerMeter: number | null;
  /** For each polyline (aligned to `polylines`), the index of its layer in `layers`. */
  polylineLayers: number[];
  /** Per-polyline source metadata (type/color), aligned to `polylines`. */
  polylineMeta: DxfPolylineMeta[];
  /** Drawable text labels extracted from TEXT entities. */
  labels: DxfTextLabel[];
  /**
   * Raw-DXF → normalised-canvas mapping parameters. Lets external datasets that
   * share the source DXF coordinate space (e.g. facility analysis bboxes) be
   * projected onto the same canvas as the geometry.
   */
  transform: FloorPlanTransform;
}

// Kept as a re-export so existing editor imports remain stable while workers
// can import the small color-only module without bundling the full DXF parser.
export { aciToHex } from './dxfColor';

/**
 * Shared normaliser. Takes raw polylines in original DXF coordinate space
 * (Y-up, document units) and produces a ParsedFloorPlan in normalised canvas
 * space: Y-flipped, translated to origin, and uniformly scaled so the longest
 * dimension equals TARGET_CANVAS_UNITS.
 */
/**
 * Shared geometry normaliser. `rawLayerIdx` (optional) is a parallel array that
 * maps each input polyline to a layer index; the returned `polylineLayers` is
 * the same mapping but aligned to the kept/output polylines (degenerate
 * polylines are dropped, so the indices must be re-tracked).
 */
/**
 * Maps a raw DXF point into normalised canvas space, mirroring the polyline
 * transform: Y is flipped, then both axes are translated to origin and scaled.
 */
export interface FloorPlanTransform {
  rawMinX: number;
  rawMinY: number;
  invScale: number;
}

interface BuiltFloorPlan {
  polylines: number[][];
  bounds: FloorPlanBounds;
  scaleFactor: number;
  polylineLayers: number[];
  /** Indices into the original input array that survived (degenerate ones dropped). */
  keptIndices: number[];
  /** Parameters to map any other raw point into the same normalised space. */
  transform: FloorPlanTransform;
}

function buildFloorPlan(
  rawPolylinesYUp: number[][],
  rawLayerIdx?: number[]
): BuiltFloorPlan {
  const raw: number[][] = [];
  const keptLayerIdx: number[] = [];
  const keptIndices: number[] = [];
  let rawMinX = Infinity, rawMinY = Infinity;
  let rawMaxX = -Infinity, rawMaxY = -Infinity;

  for (let k = 0; k < rawPolylinesYUp.length; k++) {
    const pl = rawPolylinesYUp[k];
    const flat: number[] = [];
    for (let i = 0; i + 1 < pl.length; i += 2) {
      const x = pl[i];
      const y = -pl[i + 1]; // Y-flip (DXF is Y-up, canvas is Y-down)
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      flat.push(x, y);
      if (x < rawMinX) rawMinX = x;
      if (x > rawMaxX) rawMaxX = x;
      if (y < rawMinY) rawMinY = y;
      if (y > rawMaxY) rawMaxY = y;
    }
    if (flat.length >= 4) {
      raw.push(flat);
      keptLayerIdx.push(rawLayerIdx ? rawLayerIdx[k] ?? 0 : 0);
      keptIndices.push(k);
    }
  }

  // Degenerate / empty input
  if (!Number.isFinite(rawMinX) || raw.length === 0) {
    return {
      polylines: [],
      bounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
      scaleFactor: 1,
      polylineLayers: [],
      keptIndices: [],
      transform: { rawMinX: 0, rawMinY: 0, invScale: 1 },
    };
  }

  const rawW = rawMaxX - rawMinX;
  const rawH = rawMaxY - rawMinY;
  const longestRaw = Math.max(rawW, rawH);
  const scaleFactor = longestRaw > 0 ? longestRaw / TARGET_CANVAS_UNITS : 1;
  const invScale = 1 / scaleFactor;

  const result: number[][] = [];
  for (const flat of raw) {
    const norm: number[] = [];
    for (let i = 0; i < flat.length; i += 2) {
      norm.push((flat[i] - rawMinX) * invScale, (flat[i + 1] - rawMinY) * invScale);
    }
    result.push(norm);
  }

  const bounds: FloorPlanBounds = {
    minX: 0,
    minY: 0,
    maxX: rawW * invScale,
    maxY: rawH * invScale,
  };

  return {
    polylines: result,
    bounds,
    scaleFactor,
    polylineLayers: keptLayerIdx,
    keptIndices,
    transform: { rawMinX, rawMinY, invScale },
  };
}

export function cleanMtext(text: string): string {
  return text
    .replace(/\\P/g, '\n')
    .replace(/\\[A-Za-z][^;]*;/g, '')
    .replace(/[{}]/g, '')
    .trim();
}

export function customDenormalise(parsed: {
  entities?: any[];
  blocks?: Array<{ name?: string; entities?: any[] }>;
}): any[] {
  const entities = parsed.entities ?? [];
  const blocks = parsed.blocks ?? [];
  const blocksByName = new Map<string, any>();
  for (const block of blocks) {
    if (block?.name) blocksByName.set(block.name, block);
  }

  const gatherEntities = (ents: any[], currentInsertLayer: string): any[] => {
    let current: any[] = [];
    ents.forEach((e) => {
      if (!e) return;
      if (e.type === 'INSERT') {
        const insert = e;
        const block = blocksByName.get(insert.block);
        if (!block) return;

        const rowCount = insert.rowCount ?? 1;
        const columnCount = insert.columnCount ?? 1;

        const resolvedInsertLayer = (insert.layer && insert.layer !== '0')
          ? insert.layer
          : (currentInsertLayer || '0');

        for (let r = 0; r < rowCount; r++) {
          for (let c = 0; c < columnCount; c++) {
            current = current.concat(gatherEntities(block.entities ?? [], resolvedInsertLayer));
          }
        }
      } else {
        const resolvedLayer = (e.layer && e.layer !== '0')
          ? e.layer
          : (currentInsertLayer || '0');

        current.push({
          ...e,
          layer: resolvedLayer,
        });
      }
    });
    return current;
  };

  return gatherEntities(entities, '0');
}

// ─── Drawing calibration glue ────────────────────────────────────────────────

/** Measured length (drawing units) annotated by a DIMENSION entity. */
function dimensionMeasuredLength(e: any): number {
  const ms = e?.measureStart;
  const me = e?.measureEnd;
  if (!ms || !me) return 0;
  const dx = (me.x ?? 0) - (ms.x ?? 0);
  const dy = (me.y ?? 0) - (ms.y ?? 0);
  const dimType = e?.dimensionType ?? 0;
  // Aligned (1): true distance between the definition points.
  if (dimType === 1) return Math.hypot(dx, dy);
  // Rotated / horizontal / vertical (0): project onto the dimension axis so the
  // measured value matches what the dimension text displays.
  if (dimType === 0) {
    const a = ((e?.rotation ?? 0) * Math.PI) / 180;
    return Math.abs(dx * Math.cos(a) + dy * Math.sin(a));
  }
  // Radius / diameter / angular / ordinate carry no usable linear scale here.
  return 0;
}

/**
 * Builds calibration samples from DIMENSION entities in a `dxf`-library parse
 * result. Each dimension references an anonymous block that holds the rendered
 * dimension graphics and the displayed measurement text (MTEXT/TEXT).
 */
function extractDimensionSamples(parsed: any): DimensionSample[] {
  const blocks: any[] = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
  const blockByName = new Map<string, any>();
  for (const b of blocks) if (b?.name) blockByName.set(b.name, b);

  const entities: any[] = Array.isArray(parsed?.entities) ? parsed.entities : [];
  const samples: DimensionSample[] = [];
  for (const e of entities) {
    if (e?.type !== 'DIMENSION') continue;
    const measured = dimensionMeasuredLength(e);
    if (!(measured > 0)) continue;
    const texts: string[] = [];
    const block = e?.block ? blockByName.get(e.block) : undefined;
    if (Array.isArray(block?.entities)) {
      for (const be of block.entities) {
        if ((be?.type === 'MTEXT' || be?.type === 'TEXT') && typeof be.string === 'string') {
          texts.push(be.string);
        }
      }
    }
    if (texts.length > 0) samples.push({ measuredDrawingUnits: measured, texts });
  }
  return samples;
}

/**
 * Projects a resolved calibration onto the legacy `dxfUnit`/`dxfUnitsPerMeter`
 * fields. When unknown, returns a harmless 'mm' placeholder with a 0 ratio so
 * downstream code falls back to its default instead of assuming millimetres.
 */
function legacyUnitFields(cal: DrawingCalibration): {
  dxfUnit: 'mm' | 'cm' | 'm' | 'in' | 'ft';
  dxfUnitsPerMeter: number;
} {
  if (cal.resolved && cal.drawingUnitsPerMeter) {
    return { dxfUnit: cal.unit ?? 'mm', dxfUnitsPerMeter: cal.drawingUnitsPerMeter };
  }
  return { dxfUnit: 'mm', dxfUnitsPerMeter: 0 };
}

export function parseDxf(contents: string): ParsedFloorPlan {
  const parsed = parseString(contents);
  // Determine the physical scale before normalisation: prefer the explicit
  // $INSUNITS header, then fall back to DIMENSION entities.
  // The `dxf` library exposes the header value as `header.insUnits` (camelCase);
  // the raw `$INSUNITS` spelling is kept as a defensive fallback.
  const insUnitsRaw =
    (parsed as any)?.header?.insUnits ??
    (parsed as any)?.header?.$INSUNITS ??
    (parsed as any)?.header?.INSUNITS ??
    null;
  const calibration = calibrateDrawing({
    insUnits: insUnitsRaw,
    dimensions: extractDimensionSamples(parsed),
  });
  const { dxfUnit, dxfUnitsPerMeter } = legacyUnitFields(calibration);
  const { polylines } = toPolylines(parsed);
  const entities = denormalise(parsed) as any[];
  const resolvedEntities = customDenormalise(parsed as {
    entities?: any[];
    blocks?: Array<{ name?: string; entities?: any[] }>;
  });

  // Build a working list of layers from the document declaration, plus a
  // name → index lookup so entities can be assigned in one pass. Entities that
  // reference a layer not present in the declaration are added on the fly.
  const layerDefs: DxfLayerInfo[] = [];
  const layerIndexByName = new Map<string, number>();

  const ensureLayer = (name: string, color?: number): number => {
    const key = name || '0';
    const existing = layerIndexByName.get(key);
    if (existing !== undefined) return existing;
    const idx = layerDefs.length;
    layerIndexByName.set(key, idx);
    layerDefs.push({
      name: key,
      color: color ?? 7,
      visible: true,
      count: 0,
    });
    return idx;
  };

  const tables = (parsed as any)?.tables;
  if (tables && tables.layers) {
    for (const name of Object.keys(tables.layers)) {
      const l = tables.layers[name];
      if (!l || !l.name) continue;
      const layerColorRaw =
        typeof l.colorNumber === 'number'
          ? l.colorNumber
          : typeof l.color === 'number'
            ? l.color
            : undefined;
      const layerColor = typeof layerColorRaw === 'number' ? Math.abs(layerColorRaw) : undefined;
      const idx = ensureLayer(l.name, layerColor);
      const def = layerDefs[idx];
      console.log(layerDefs[idx]);
      const flags = l.flags ?? 0;
      def.frozen = (flags & 1) !== 0;
      def.locked = (flags & 4) !== 0;

      const isOff = typeof layerColorRaw === 'number' && layerColorRaw < 0;
      if (typeof layerColor === 'number') {
        def.color = layerColor;
      }
      def.visible = !isOff && !def.frozen;
      console.log(def);
    }
  }

  const rawPolylines: number[][] = [];
  const rawLayerIdx: number[] = [];
  const rawTypes: string[] = [];
  const rawColors: number[] = [];

  for (let i = 0; i < polylines.length; i++) {
    const pl = polylines[i];
    if (!pl.vertices || pl.vertices.length < 2) continue;
    const flat: number[] = [];
    for (const v of pl.vertices) flat.push(v[0], v[1]);
    rawPolylines.push(flat);

    const ent = entities[i];
    const resolvedEnt = resolvedEntities[i];
    const layerName = resolvedEnt?.layer || '0';
    const entColorRaw =
      typeof ent?.colorNumber === 'number'
        ? ent.colorNumber
        : typeof ent?.color === 'number'
          ? ent.color
          : 256;
    const entColor = Math.abs(entColorRaw);
    const entType = ent?.type || 'POLYLINE';

    rawLayerIdx.push(ensureLayer(layerName, entColor));
    rawTypes.push(entType);
    rawColors.push(entColor);
  }

  const built = buildFloorPlan(rawPolylines, rawLayerIdx);

  // Re-derive per-polyline metadata aligned to the kept (non-degenerate) output.
  const builtMeta: DxfPolylineMeta[] = built.keptIndices.map((srcIdx) => ({
    type: rawTypes[srcIdx],
    color: rawColors[srcIdx],
  }));

  interface RawLabel {
    x: number;
    y: number;
    text: string;
    height: number;
    rotation: number;
    layerIndex: number;
    color: number;
  }
  const rawLabels: RawLabel[] = [];

  for (let i = 0; i < entities.length; i++) {
    const ent = entities[i];
    if (ent?.type === 'TEXT') {
      const textVal = ent.string;
      if (typeof textVal === 'string' && textVal.trim().length > 0) {
        // Transform insertion point
        let tx = ent.x || 0;
        let ty = ent.y || 0;
        let totalRotation = ent.rotation || 0;
        let totalScale = 1;

        if (ent.transforms) {
          let pt = [tx, ty];
          for (const transform of ent.transforms) {
            if (transform.scaleX !== undefined) {
              pt[0] *= transform.scaleX;
              totalScale *= Math.abs(transform.scaleX);
            }
            if (transform.scaleY !== undefined) {
              pt[1] *= transform.scaleY;
            }
            if (transform.rotation) {
              const angle = (transform.rotation / 180) * Math.PI;
              const rx = pt[0] * Math.cos(angle) - pt[1] * Math.sin(angle);
              const ry = pt[1] * Math.cos(angle) + pt[0] * Math.sin(angle);
              pt = [rx, ry];
              totalRotation += transform.rotation;
            }
            if (transform.x !== undefined) {
              pt[0] += transform.x;
            }
            if (transform.y !== undefined) {
              pt[1] += transform.y;
            }
            if (transform.extrusionZ === -1) {
              pt[0] = -pt[0];
            }
          }
          tx = pt[0];
          ty = pt[1];
        }

        const resolvedEnt = resolvedEntities[i];
        const layerName = resolvedEnt?.layer || '0';
        const entColorRaw =
          typeof ent?.colorNumber === 'number'
            ? ent.colorNumber
            : typeof ent?.color === 'number'
              ? ent.color
              : 256;
        const entColor = Math.abs(entColorRaw);

        rawLabels.push({
          x: tx,
          y: ty,
          text: textVal,
          height: (ent.textHeight || 0) * totalScale,
          rotation: totalRotation,
          layerIndex: ensureLayer(layerName, entColor),
          color: entColor,
        });
      }
    }
  }

  // Transform label insertion points into the same normalised canvas space as
  // the geometry (Y-flip, translate to origin, uniform scale).
  const { rawMinX, rawMinY, invScale } = built.transform;
  const builtLabels: (DxfTextLabel & { _layerDefIdx: number })[] = rawLabels.map((l) => ({
    x: (l.x - rawMinX) * invScale,
    y: (-l.y - rawMinY) * invScale,
    text: l.text,
    height: l.height * invScale,
    // Source rotation is CCW in Y-up space; negate for the Y-down canvas.
    rotation: -l.rotation,
    color: l.color,
    layerIndex: 0,
    _layerDefIdx: l.layerIndex,
  }));

  // Count drawable polylines per layer. A layer is kept if it has geometry OR
  // at least one label, then `polylineLayers`/labels are re-indexed to the
  // compacted list (keeps the panel focused on layers the user can see).
  const counts = new Array(layerDefs.length).fill(0);
  for (const li of built.polylineLayers) counts[li]++;
  const labelCounts = new Array(layerDefs.length).fill(0);
  for (const l of builtLabels) labelCounts[l._layerDefIdx]++;

  const remap = new Array(layerDefs.length).fill(-1);
  const layers: DxfLayerInfo[] = [];
  for (let i = 0; i < layerDefs.length; i++) {
    if (counts[i] === 0 && labelCounts[i] === 0) continue;
    remap[i] = layers.length;
    layers.push({ ...layerDefs[i], count: counts[i] });
  }

  const polylineLayers = built.polylineLayers.map((li) => remap[li]);
  const labels: DxfTextLabel[] = builtLabels.map(({ _layerDefIdx, ...rest }) => ({
    ...rest,
    layerIndex: remap[_layerDefIdx],
  }));

  const canvasCal = deriveCanvasCalibration(calibration, built.scaleFactor);

  return {
    polylines: built.polylines,
    bounds: built.bounds,
    scaleFactor: built.scaleFactor,
    polylineLayers,
    layers,
    dxfUnit,
    dxfUnitsPerMeter,
    calibration,
    millimetersPerCanvasUnit: canvasCal.millimetersPerCanvasUnit,
    canvasUnitsPerMeter: canvasCal.canvasUnitsPerMeter,
    polylineMeta: builtMeta,
    labels,
    transform: built.transform,
  };
}

// ─── DXF-as-JSON import (external parser output) ──────────────────────────────

/** Number of straight segments used to tessellate a full 360° circle. */
const CIRCLE_SEGMENTS = 48;
/** Approximate degrees of arc covered by each tessellated segment. */
const DEGREES_PER_ARC_SEGMENT = 6;

/** Nested classification object attached to backend / parser entities. */
export type DxfEntityCategory = {
  primary_category?: string | null;
  sub_category?: string | null;
  equipment_type?: string | null;
  tool_name?: string | null;
  tool_number?: string | null;
  work_cell?: string | null;
  category_confidence?: string | null;
};

/** One geometric entity as emitted by the external DXF→JSON parser. */
export interface DxfJsonEntity {
  type: string; // LINE | LWPOLYLINE | POLYLINE | ARC | CIRCLE | TEXT | MTEXT | ...
  layer?: string;
  color?: number;
  /** Nested classification (`category.primary_category` / `category.sub_category`). */
  category?: DxfEntityCategory | string;
  primary_category?: string;
  sub_category?: string;
  // Shape depends on `type` — see tessellation switch below.
  coordinates: any[];
  properties?: Record<string, unknown>;
}

/** Reads primary/sub category from flat fields or nested `category` objects. */
function readEntityClassification(
  ent: {
    category?: DxfEntityCategory | string | null;
    primary_category?: unknown;
    sub_category?: unknown;
    properties?: Record<string, unknown> | null;
  } | null | undefined,
  fallback?: { category?: string; subcategory?: string }
): { category?: string; subcategory?: string } {
  const nested =
    ent?.category && typeof ent.category === 'object'
      ? (ent.category as DxfEntityCategory)
      : null;
  const props = ent?.properties && typeof ent.properties === 'object' ? ent.properties : null;
  const nestedProps =
    props?.category && typeof props.category === 'object'
      ? (props.category as DxfEntityCategory)
      : null;

  const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

  const category =
    asString(nested?.primary_category) ??
    asString(nestedProps?.primary_category) ??
    asString(ent?.primary_category) ??
    asString(props?.primary_category) ??
    (typeof ent?.category === 'string' ? asString(ent.category) : undefined) ??
    fallback?.category;

  const subcategory =
    asString(nested?.sub_category) ??
    asString(nestedProps?.sub_category) ??
    asString(ent?.sub_category) ??
    asString(props?.sub_category) ??
    fallback?.subcategory;

  return { category, subcategory };
}

export interface DxfJsonLayer {
  id?: string;
  layer_id?: string;
  uuid?: string;
  name: string;
  category?: string;
  subcategory?: string;
  sub_category?: string;
  subCategory?: string;
  color?: number;
  /** Preferred visibility flag from backend / lossless JSON. */
  visible?: boolean;
  is_visible?: boolean;
  is_frozen?: boolean;
  is_locked?: boolean;
}

/** Prefer backend UUID fields; fall back to layer name when id is absent. */
export function resolveDxfLayerId(layer: {
  id?: string | null;
  layer_id?: string | null;
  uuid?: string | null;
  name?: string;
}): string | undefined {
  for (const candidate of [layer.id, layer.layer_id, layer.uuid]) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  if (typeof layer.name === 'string' && layer.name.trim()) return layer.name.trim();
  return undefined;
}

/** Top-level document shape returned by the external DXF→JSON parser / API. */
export interface DxfJsonDocument {
  id?: string;
  name?: string;
  file_name?: string;
  units?: string | number;
  bounding_box?: {
    min_x: number;
    min_y: number;
    max_x: number;
    max_y: number;
    width?: number;
    height?: number;
  };
  entity_count?: number;
  layer_count?: number;
  layers?: DxfJsonLayer[];
  entities: DxfJsonEntity[];
  metadata?: Record<string, unknown>;
}

/**
 * Best-effort calibration samples from DIMENSION entities in an external-parser
 * JSON document. The external parser's DIMENSION shape is not fully specified,
 * so several common field spellings are probed; nothing is assumed when absent.
 */
function extractJsonDimensionSamples(entities: DxfJsonEntity[]): DimensionSample[] {
  const num = (v: any): number | undefined => (Number.isFinite(v) ? Number(v) : undefined);
  const pointXY = (p: any): [number, number] | null => {
    if (!p) return null;
    const x = num(p.x ?? p[0]);
    const y = num(p.y ?? p[1]);
    return x !== undefined && y !== undefined ? [x, y] : null;
  };

  const samples: DimensionSample[] = [];
  for (const ent of entities) {
    if (ent?.type !== 'DIMENSION') continue;
    const p = (ent.properties ?? {}) as Record<string, any>;

    let measured = num(p.measurement ?? p.measured ?? p.actualMeasurement) ?? 0;
    measured = Math.abs(measured);
    if (!(measured > 0)) {
      const ms = pointXY(p.measureStart ?? p.defPoint1 ?? p.def_point_1);
      const me = pointXY(p.measureEnd ?? p.defPoint2 ?? p.def_point_2);
      if (ms && me) measured = Math.hypot(me[0] - ms[0], me[1] - ms[1]);
    }
    if (!(measured > 0)) continue;

    const texts: string[] = [];
    for (const key of ['text', 'displayText', 'measurementText', 'value', 'label']) {
      const v = p[key];
      if (typeof v === 'string' && v.trim()) texts.push(v);
    }
    if (texts.length > 0) samples.push({ measuredDrawingUnits: measured, texts });
  }
  return samples;
}

function tessellateArc(
  cx: number,
  cy: number,
  radius: number,
  startDeg: number,
  endDeg: number
): number[] {
  // DXF arcs sweep counter-clockwise from start to end angle.
  let sweep = endDeg - startDeg;
  while (sweep <= 0) sweep += 360;
  const segments = Math.max(2, Math.ceil(sweep / DEGREES_PER_ARC_SEGMENT));
  const pts: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = ((startDeg + (sweep * i) / segments) * Math.PI) / 180;
    pts.push(cx + radius * Math.cos(a), cy + radius * Math.sin(a));
  }
  return pts;
}

function tessellateCircle(cx: number, cy: number, radius: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
    const a = (i / CIRCLE_SEGMENTS) * 2 * Math.PI;
    pts.push(cx + radius * Math.cos(a), cy + radius * Math.sin(a));
  }
  return pts;
}

/**
 * Converts the JSON document produced by the external DXF parser (whether read
 * from a file or returned by an API) into a renderable ParsedFloorPlan.
 *
 * Each entity's `coordinates` field is tessellated into a flat polyline:
 *   LINE        → [[x0,y0],[x1,y1]]
 *   LWPOLYLINE  → [[x,y], ...]
 *   ARC         → [[cx,cy], radius, startAngleDeg, endAngleDeg]
 *   CIRCLE      → [[cx,cy], radius]
 *   TEXT/MTEXT  → ignored (no drawable geometry)
 */
export function parseDxfJson(doc: DxfJsonDocument): ParsedFloorPlan {
  const entities = Array.isArray(doc?.entities) ? doc.entities : [];
  // Prefer the document's declared units; otherwise fall back to DIMENSION
  // entities. Never assume millimetres when nothing resolves.
  const insUnitsRaw =
    doc?.units ??
    (doc?.metadata?.units as number | string | undefined) ??
    (doc?.metadata?.insunits as number | string | undefined) ??
    (doc?.metadata?.INSUNITS as number | string | undefined) ??
    null;
  const calibration = calibrateDrawing({
    insUnits: insUnitsRaw,
    dimensions: extractJsonDimensionSamples(entities),
  });
  const { dxfUnit, dxfUnitsPerMeter } = legacyUnitFields(calibration);
  const declaredLayers = Array.isArray(doc?.layers) ? doc.layers : [];

  // Build a working list of layers from the document declaration, plus a
  // name → index lookup so entities can be assigned in one pass. Entities that
  // reference a layer not present in the declaration are added on the fly.
  const layerDefs: DxfLayerInfo[] = [];
  const layerIndexByName = new Map<string, number>();

  const ensureLayer = (name: string, color?: number): number => {
    const key = name || '0';
    const existing = layerIndexByName.get(key);
    if (existing !== undefined) return existing;
    const idx = layerDefs.length;
    layerIndexByName.set(key, idx);
    layerDefs.push({
      name: key,
      color: color ?? 7,
      visible: true,
      count: 0,
    });
    return idx;
  };

  for (const l of declaredLayers) {
    if (!l?.name) continue;
    const idx = ensureLayer(l.name, l.color);
    const def = layerDefs[idx];
    const resolvedId = resolveDxfLayerId(l);
    // Keep a real UUID when present; don't overwrite with name-only fallback yet.
    if (resolvedId && resolvedId !== l.name) {
      def.id = resolvedId;
    } else if (typeof l.id === 'string' && l.id.trim()) {
      def.id = l.id.trim();
    } else if (typeof l.layer_id === 'string' && l.layer_id.trim()) {
      def.id = l.layer_id.trim();
    } else if (typeof l.uuid === 'string' && l.uuid.trim()) {
      def.id = l.uuid.trim();
    }
    def.color = l.color ?? def.color;
    def.category = l.category;
    def.subcategory = l.subcategory ?? l.sub_category ?? l.subCategory;
    def.frozen = l.is_frozen;
    def.locked = l.is_locked;
    // Prefer explicit `visible`, then `is_visible`. The backend already hides
    // frozen layers at import, and a saved visibility must win over the DXF flag.
    const explicitVisible =
      typeof l.visible === 'boolean'
        ? l.visible
        : typeof l.is_visible === 'boolean'
          ? l.is_visible
          : true;
    def.visible = explicitVisible;
  }

  const rawPolylines: number[][] = [];
  const rawLayerIdx: number[] = [];
  // Parallel arrays carrying per-polyline provenance through the geometry
  // normaliser, which may drop degenerate entries (re-tracked via keptIndices).
  const rawTypes: string[] = [];
  const rawColors: number[] = [];
  const rawCategories: Array<string | undefined> = [];
  const rawSubcategories: Array<string | undefined> = [];

  // Text labels in raw DXF space, transformed to canvas space after the
  // geometry pass establishes the shared origin/scale.
  interface RawLabel {
    x: number;
    y: number;
    text: string;
    height: number;
    rotation: number;
    layerIndex: number;
    color: number;
  }
  const rawLabels: RawLabel[] = [];

  const push = (
    pts: number[],
    layerName: string,
    color: number | undefined,
    type: string,
    category?: string,
    subcategory?: string
  ) => {
    rawPolylines.push(pts);
    rawLayerIdx.push(ensureLayer(layerName, color));
    rawTypes.push(type);
    rawColors.push(color ?? 256);
    rawCategories.push(category);
    rawSubcategories.push(subcategory);
  };

  for (const ent of entities) {
    const c = ent?.coordinates;
    if (!Array.isArray(c) || c.length === 0) continue;
    const layerName = ent.layer || '0';
    const { category, subcategory } = readEntityClassification(ent);

    switch (ent.type) {
      case 'LINE': {
        const [p0, p1] = c;
        if (Array.isArray(p0) && Array.isArray(p1)) {
          push([p0[0], p0[1], p1[0], p1[1]], layerName, ent.color, 'LINE', category, subcategory);
        }
        break;
      }

      case 'LWPOLYLINE':
      case 'POLYLINE': {
        const flat: number[] = [];
        for (const p of c) {
          if (Array.isArray(p) && p.length >= 2) flat.push(p[0], p[1]);
        }
        if (flat.length >= 4) push(flat, layerName, ent.color, ent.type, category, subcategory);
        break;
      }

      case 'ARC': {
        // Accept both ARC encodings:
        // 1) [ [cx,cy], radius, startDeg, endDeg ]
        // 2) [ [x0,y0], [x1,y1], ... ] (already tessellated)
        if (Array.isArray(c[0]) && Array.isArray(c[1])) {
          const flat: number[] = [];
          for (const p of c) {
            if (Array.isArray(p) && p.length >= 2) flat.push(p[0], p[1]);
          }
          if (flat.length >= 4) push(flat, layerName, ent.color, 'ARC', category, subcategory);
          break;
        }
        const [center, radius, startDeg, endDeg] = c;
        if (Array.isArray(center) && Number.isFinite(radius)) {
          push(
            tessellateArc(center[0], center[1], radius, startDeg ?? 0, endDeg ?? 360),
            layerName,
            ent.color,
            'ARC',
            category,
            subcategory
          );
        }
        break;
      }

      case 'CIRCLE': {
        // Accept both CIRCLE encodings:
        // 1) [ [cx,cy], radius ]
        // 2) [ [x0,y0], [x1,y1], ... ] (already tessellated)
        if (Array.isArray(c[0]) && Array.isArray(c[1])) {
          const flat: number[] = [];
          for (const p of c) {
            if (Array.isArray(p) && p.length >= 2) flat.push(p[0], p[1]);
          }
          if (flat.length >= 4) push(flat, layerName, ent.color, 'CIRCLE', category, subcategory);
          break;
        }
        const [center, radius] = c;
        if (Array.isArray(center) && Number.isFinite(radius)) {
          push(
            tessellateCircle(center[0], center[1], radius),
            layerName,
            ent.color,
            'CIRCLE',
            category,
            subcategory
          );
        }
        break;
      }

      // TEXT carries a drawable string at a known insertion point. The parser
      // emits coordinates as [[x, y], "string", height, rotationDeg].
      case 'TEXT': {
        const [pos, str, height, rotation] = c as [
          [number, number],
          string,
          number,
          number
        ];
        if (
          Array.isArray(pos) &&
          typeof str === 'string' &&
          str.trim().length > 0
        ) {
          rawLabels.push({
            x: pos[0],
            y: pos[1],
            text: str,
            height: Number.isFinite(height) ? (height as number) : 0,
            rotation: Number.isFinite(rotation) ? (rotation as number) : 0,
            layerIndex: ensureLayer(layerName, ent.color),
            color: ent.color ?? 256,
          });
        }
        break;
      }

      // MTEXT (empty coordinates from this parser) and other annotation-only
      // entities carry no usable geometry, so they are skipped.
      default:
        break;
    }
  }

  const built = buildFloorPlan(rawPolylines, rawLayerIdx);

  // Re-derive per-polyline metadata aligned to the kept (non-degenerate) output.
  const builtMeta: DxfPolylineMeta[] = built.keptIndices.map((srcIdx) => ({
    type: rawTypes[srcIdx],
    color: rawColors[srcIdx],
    category: rawCategories[srcIdx],
    subcategory: rawSubcategories[srcIdx],
  }));

  // Transform label insertion points into the same normalised canvas space as
  // the geometry (Y-flip, translate to origin, uniform scale).
  const { rawMinX, rawMinY, invScale } = built.transform;
  const builtLabels: (DxfTextLabel & { _layerDefIdx: number })[] = rawLabels.map((l) => ({
    x: (l.x - rawMinX) * invScale,
    y: (-l.y - rawMinY) * invScale,
    text: l.text,
    height: l.height * invScale,
    // Source rotation is CCW in Y-up space; negate for the Y-down canvas.
    rotation: -l.rotation,
    color: l.color,
    layerIndex: 0,
    _layerDefIdx: l.layerIndex,
  }));

  // Count drawable polylines per layer. A layer is kept if it has geometry OR
  // at least one label, then `polylineLayers`/labels are re-indexed to the
  // compacted list (keeps the panel focused on layers the user can see).
  const counts = new Array(layerDefs.length).fill(0);
  for (const li of built.polylineLayers) counts[li]++;
  const labelCounts = new Array(layerDefs.length).fill(0);
  for (const l of builtLabels) labelCounts[l._layerDefIdx]++;

  const remap = new Array(layerDefs.length).fill(-1);
  const layers: DxfLayerInfo[] = [];
  for (let i = 0; i < layerDefs.length; i++) {
    if (counts[i] === 0 && labelCounts[i] === 0) continue;
    remap[i] = layers.length;
    layers.push({ ...layerDefs[i], count: counts[i] });
  }

  const polylineLayers = built.polylineLayers.map((li) => remap[li]);
  const labels: DxfTextLabel[] = builtLabels.map(({ _layerDefIdx, ...rest }) => ({
    ...rest,
    layerIndex: remap[_layerDefIdx],
  }));

  const canvasCal = deriveCanvasCalibration(calibration, built.scaleFactor);

  return {
    polylines: built.polylines,
    bounds: built.bounds,
    scaleFactor: built.scaleFactor,
    layers,
    dxfUnit,
    dxfUnitsPerMeter,
    calibration,
    millimetersPerCanvasUnit: canvasCal.millimetersPerCanvasUnit,
    canvasUnitsPerMeter: canvasCal.canvasUnitsPerMeter,
    polylineLayers,
    polylineMeta: builtMeta,
    labels,
    transform: built.transform,
  };
}

/**
 * Converts backend DXF JSON (with LINE.start/end, LWPOLYLINE.points, ARC.x/y/r)
 * into the external-parser shape consumed by parseDxfJson.
 */
export function parseBackendDxfJson(doc: {
  entities?: any[];
  blocks?: Array<{ name?: string; x?: number; y?: number; entities?: any[] }>;
  tables?: { layers?: { layers?: any[] } | any[] | Record<string, any> };
  header?: Record<string, unknown>;
}): ParsedFloorPlan {
  const entities = Array.isArray(doc?.entities) ? doc.entities : [];
  const blocks = Array.isArray(doc?.blocks) ? doc.blocks : [];
  const blocksByName = new Map<string, { x?: number; y?: number; entities?: any[] }>();
  for (const block of blocks) {
    if (block?.name) blocksByName.set(block.name, block);
  }

  type Affine2D = { a: number; b: number; c: number; d: number; tx: number; ty: number };
  const identity: Affine2D = { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };

  const composeAffine = (parent: Affine2D, local: Affine2D): Affine2D => ({
    a: parent.a * local.a + parent.c * local.b,
    b: parent.b * local.a + parent.d * local.b,
    c: parent.a * local.c + parent.c * local.d,
    d: parent.b * local.c + parent.d * local.d,
    tx: parent.a * local.tx + parent.c * local.ty + parent.tx,
    ty: parent.b * local.tx + parent.d * local.ty + parent.ty,
  });

  const applyPoint = (t: Affine2D, x: number, y: number): [number, number] => [
    t.a * x + t.c * y + t.tx,
    t.b * x + t.d * y + t.ty,
  ];

  const rotationDegFromAffine = (t: Affine2D): number => (Math.atan2(t.b, t.a) * 180) / Math.PI;

  const averageScaleFromAffine = (t: Affine2D): number => {
    const sx = Math.hypot(t.a, t.b);
    const sy = Math.hypot(t.c, t.d);
    if (!Number.isFinite(sx) || !Number.isFinite(sy)) return 1;
    return (sx + sy) / 2 || 1;
  };

  const converted: DxfJsonEntity[] = [];

  const num = (v: unknown): number | null => (Number.isFinite(v) ? Number(v) : null);

  const toPoint = (p: any): [number, number] | null => {
    if (!p) return null;
    const x = num(p.x ?? p[0]);
    const y = num(p.y ?? p[1]);
    if (x === null || y === null) return null;
    return [x, y];
  };

  const convertEntity = (
    ent: any,
    transform: Affine2D,
    inheritedLayer: string,
    depth: number,
    stack: Set<string>,
    inheritedCategory?: string,
    inheritedSubcategory?: string
  ): void => {
    if (!ent || typeof ent.type !== 'string' || depth > 20) return;
    const ownLayer = typeof ent.layer === 'string' ? ent.layer : '0';
    const layer = ownLayer && ownLayer !== '0' ? ownLayer : inheritedLayer || '0';
    const color = num(ent.color);
    const { category, subcategory } = readEntityClassification(ent, {
      category: inheritedCategory,
      subcategory: inheritedSubcategory,
    });
    const classification = {
      primary_category: category,
      sub_category: subcategory,
      category:
        category || subcategory
          ? {
            primary_category: category ?? null,
            sub_category: subcategory ?? null,
          }
          : undefined,
    };

    if (ent.type === 'INSERT') {
      const blockName = typeof ent.block === 'string' ? ent.block : '';
      if (!blockName || stack.has(blockName)) return;
      const block = blocksByName.get(blockName);
      if (!block || !Array.isArray(block.entities)) return;

      const sx = num(ent.scaleX) ?? 1;
      const sy = num(ent.scaleY) ?? 1;
      const rot = ((num(ent.rotation) ?? 0) * Math.PI) / 180;
      const cos = Math.cos(rot);
      const sin = Math.sin(rot);
      const baseX = num(block.x) ?? 0;
      const baseY = num(block.y) ?? 0;
      const ix = num(ent.x) ?? 0;
      const iy = num(ent.y) ?? 0;

      const insertLocal: Affine2D = {
        a: cos * sx,
        b: sin * sx,
        c: -sin * sy,
        d: cos * sy,
        tx: ix - (cos * sx) * baseX - (-sin * sy) * baseY,
        ty: iy - (sin * sx) * baseX - (cos * sy) * baseY,
      };

      const rowCount = Math.max(1, Math.floor(num(ent.rowCount) ?? 1));
      const colCount = Math.max(1, Math.floor(num(ent.columnCount) ?? 1));
      const rowSpacing = num(ent.rowSpacing) ?? 0;
      const colSpacing = num(ent.columnSpacing) ?? 0;
      const resolvedLayer = layer;

      stack.add(blockName);
      for (let r = 0; r < rowCount; r++) {
        for (let c = 0; c < colCount; c++) {
          const arrayOffset: Affine2D = {
            a: 1,
            b: 0,
            c: 0,
            d: 1,
            tx: c * colSpacing,
            ty: r * rowSpacing,
          };
          const instanceTransform = composeAffine(transform, composeAffine(insertLocal, arrayOffset));
          for (const sub of block.entities) {
            convertEntity(
              sub,
              instanceTransform,
              resolvedLayer,
              depth + 1,
              stack,
              category,
              subcategory
            );
          }
        }
      }
      stack.delete(blockName);
      return;
    }

    if (ent.type === 'LINE') {
      const p0 = toPoint(ent.start);
      const p1 = toPoint(ent.end);
      if (p0 && p1) {
        const t0 = applyPoint(transform, p0[0], p0[1]);
        const t1 = applyPoint(transform, p1[0], p1[1]);
        converted.push({
          type: 'LINE',
          layer,
          color: color !== null ? Math.abs(color) : undefined,
          ...classification,
          coordinates: [t0, t1],
        });
      }
      return;
    }

    if (ent.type === 'LWPOLYLINE') {
      const pointsRaw = Array.isArray(ent.points)
        ? ent.points
          .map((pt: any) => toPoint(pt))
          .filter((point: [number, number] | null): point is [number, number] => point !== null)
        : [];
      if (pointsRaw.length >= 2) {
        const points = pointsRaw.map((pt: [number, number]) => applyPoint(transform, pt[0], pt[1]));
        const closed = Boolean(ent.closed);
        if (
          closed &&
          (points[0][0] !== points[points.length - 1][0] ||
            points[0][1] !== points[points.length - 1][1])
        ) {
          points.push([points[0][0], points[0][1]]);
        }
        converted.push({
          type: 'LWPOLYLINE',
          layer,
          color: color !== null ? Math.abs(color) : undefined,
          ...classification,
          coordinates: points,
        });
      }
      return;
    }

    if (ent.type === 'POLYLINE') {
      const pointsRaw = Array.isArray(ent.vertices)
        ? ent.vertices
          .map((pt: any) => toPoint(pt))
          .filter((point: [number, number] | null): point is [number, number] => point !== null)
        : [];
      if (pointsRaw.length >= 2) {
        const points = pointsRaw.map((pt: [number, number]) => applyPoint(transform, pt[0], pt[1]));
        const closed = Boolean(ent.closed);
        if (
          closed &&
          (points[0][0] !== points[points.length - 1][0] ||
            points[0][1] !== points[points.length - 1][1])
        ) {
          points.push([points[0][0], points[0][1]]);
        }
        converted.push({
          type: 'LWPOLYLINE',
          layer,
          color: color !== null ? Math.abs(color) : undefined,
          ...classification,
          coordinates: points,
        });
      }
      return;
    }

    if (ent.type === 'ARC') {
      const cx = num(ent.x);
      const cy = num(ent.y);
      const r = num(ent.r);
      const start = num(ent.startAngle) ?? 0;
      const end = num(ent.endAngle) ?? 360;
      if (cx !== null && cy !== null && r !== null) {
        const tess = tessellateArc(cx, cy, r, start, end);
        const transformed: [number, number][] = [];
        for (let i = 0; i + 1 < tess.length; i += 2) {
          transformed.push(applyPoint(transform, tess[i], tess[i + 1]));
        }
        converted.push({
          type: 'ARC',
          layer,
          color: color !== null ? Math.abs(color) : undefined,
          ...classification,
          coordinates: transformed,
        });
      }
      return;
    }

    if (ent.type === 'CIRCLE') {
      const cx = num(ent.x);
      const cy = num(ent.y);
      const r = num(ent.r);
      if (cx !== null && cy !== null && r !== null) {
        const tess = tessellateCircle(cx, cy, r);
        const transformed: [number, number][] = [];
        for (let i = 0; i + 1 < tess.length; i += 2) {
          transformed.push(applyPoint(transform, tess[i], tess[i + 1]));
        }
        converted.push({
          type: 'CIRCLE',
          layer,
          color: color !== null ? Math.abs(color) : undefined,
          ...classification,
          coordinates: transformed,
        });
      }
      return;
    }

    if (ent.type === 'TEXT' || ent.type === 'MTEXT') {
      const t = ent.text;
      const pos = toPoint(t);
      const value = typeof t?.string === 'string' ? t.string : '';
      if (pos && value.trim().length > 0) {
        const tp = applyPoint(transform, pos[0], pos[1]);
        const avgScale = averageScaleFromAffine(transform);
        const extraRot = rotationDegFromAffine(transform);
        converted.push({
          type: 'TEXT',
          layer,
          color: color !== null ? Math.abs(color) : undefined,
          ...classification,
          coordinates: [
            [tp[0], tp[1]],
            value,
            (num(t?.textHeight) ?? 0) * avgScale,
            (num(t?.rotation) ?? 0) + extraRot,
          ],
        });
      }
      return;
    }
  };

  const rootEntities = entities.length
    ? entities
    : (() => {
      const modelSpace = blocksByName.get('*MODEL_SPACE');
      return Array.isArray(modelSpace?.entities) ? modelSpace.entities : [];
    })();

  for (const ent of rootEntities) {
    convertEntity(ent, identity, '0', 0, new Set<string>());
  }

  const tableLayers = doc?.tables?.layers;
  const rawLayers = Array.isArray((tableLayers as any)?.layers)
    ? (tableLayers as any).layers
    : Array.isArray(tableLayers)
      ? tableLayers
      : tableLayers && typeof tableLayers === 'object'
        ? Object.values(tableLayers)
        : [];

  const layers: DxfJsonLayer[] = rawLayers
    .filter((l: any) => l && typeof l.name === 'string')
    .map((l: any) => {
      const rawColor = l.color ? Math.abs(num(l.color) ?? 0) : null; // Negative color numbers indicate "off" in AutoCAD. we will remove this condition later
      const flags = Number.isFinite(l.flags) ? Number(l.flags) : 0;
      const isFrozen = l.frozen !== undefined ? Boolean(l.frozen) : (flags & 1) !== 0;
      const isLocked = l.locked !== undefined ? Boolean(l.locked) : (flags & 4) !== 0;
      const isOff = rawColor !== null && rawColor < 0;
      return {
        id: resolveDxfLayerId(l),
        name: l.name,
        category:
          typeof l.category === 'string'
            ? l.category
            : typeof l.classification === 'string'
              ? l.classification
              : undefined,
        subcategory:
          typeof l.subcategory === 'string'
            ? l.subcategory
            : typeof l.sub_category === 'string'
              ? l.sub_category
              : typeof l.subCategory === 'string'
                ? l.subCategory
                : undefined,
        color: rawColor !== null ? Math.abs(rawColor) : undefined,
        is_visible: l.visible !== false && !isOff,
        visible: l.visible !== false && !isOff,
        is_frozen: isFrozen,
        is_locked: isLocked,
      };
    });

  const parsed = parseDxfJson({
    name: 'backend-dxf-json',
    units: (doc?.header?.$INSUNITS as string | number | undefined) ?? undefined,
    layers,
    entities: converted,
  });

  // Keep declared layers even if they currently have no drawable geometry.
  if (layers.length === 0) return parsed;
  const existing = new Set(parsed.layers.map((l) => l.name));
  const extraLayers: DxfLayerInfo[] = layers
    .filter((l) => !existing.has(l.name))
    .map((l) => {
      const explicitVisible =
        typeof l.visible === 'boolean'
          ? l.visible
          : typeof l.is_visible === 'boolean'
            ? l.is_visible
            : true;
      return {
        id: resolveDxfLayerId(l),
        name: l.name,
        color: l.color ?? 7,
        category: l.category,
        subcategory: l.subcategory ?? l.sub_category ?? l.subCategory,
        visible: explicitVisible,
        frozen: l.is_frozen,
        locked: l.is_locked,
        count: 0,
      };
    });

  return extraLayers.length > 0
    ? {
      ...parsed,
      layers: [...parsed.layers, ...extraLayers],
    }
    : parsed;
}
